import { useMemo } from 'react'

/**
 * Custom hook for personalizing labels throughout the app
 * Each user sees labels from their own perspective:
 * - Tom sees: "You paid", "Sarah paid", "Sarah owes you"
 * - Sarah sees: "You paid", "Tom paid", "Tom owes you"
 *
 * @param {Object} couple - Couple object with members array
 * @param {string} currentUserId - Current logged-in user's ID
 * @returns {Object} Personalization functions and partner info
 */
export function usePersonalization(couple, currentUserId) {
  return useMemo(() => {
    // Default values if couple data isn't loaded yet
    if (!couple?.members || !currentUserId) {
      return {
        partnerName: 'Partner',
        currentUserName: 'You',
        getPaidByLabel: () => 'Someone paid',
        getOwesLabel: () => '',
        getBalanceLabel: () => 'Loading...',
        getSettleUpMessage: () => '',
        isReady: false
      }
    }

    // Find partner and current user from couple members
    const partner = couple.members.find(m => m.user_id !== currentUserId)
    const currentUser = couple.members.find(m => m.user_id === currentUserId)

    return {
      partnerName: partner?.display_name || 'Partner',
      currentUserName: currentUser?.display_name || 'You',
      isReady: true,

      /**
       * Get the "who paid" label for an expense
       * @param {string} paidByUserId - User ID who paid
       * @returns {string} "You paid" or "[Partner] paid"
       */
      getPaidByLabel(paidByUserId) {
        if (paidByUserId === currentUserId) {
          return 'You paid'
        }
        return `${partner?.display_name || 'Partner'} paid`
      },

      /**
       * Get the "owes" label for an expense
       * @param {string} paidByUserId - User ID who paid
       * @param {number} amount - Amount owed
       * @returns {string} "[Partner] owes $X" or "You owe $X"
       */
      getOwesLabel(paidByUserId, amount) {
        const formattedAmount = `$${parseFloat(amount).toFixed(2)}`

        if (paidByUserId === currentUserId) {
          // I paid, so partner owes me
          return `${partner?.display_name || 'Partner'} owes ${formattedAmount}`
        } else {
          // Partner paid, so I owe them
          return `You owe ${formattedAmount}`
        }
      },

      /**
       * Get the balance label for the hero display
       * @param {number} balance - Current balance (positive = they owe you, negative = you owe them)
       * @returns {string} Balance status message
       */
      getBalanceLabel(balance) {
        if (balance === 0) {
          return 'All settled up!'
        }

        if (balance > 0) {
          // Positive balance means partner owes current user
          return `${partner?.display_name || 'Partner'} owes you`
        } else {
          // Negative balance means current user owes partner
          return `You owe ${partner?.display_name || 'Partner'}`
        }
      },

      /**
       * Get the settle up confirmation message
       * @param {number} balance - Current balance
       * @returns {string} Settle up message
       */
      getSettleUpMessage(balance) {
        const amount = Math.abs(balance).toFixed(2)

        if (balance > 0) {
          // Partner owes current user
          return `${partner?.display_name || 'Partner'} pays you $${amount}`
        } else {
          // Current user owes partner
          return `You pay ${partner?.display_name || 'Partner'} $${amount}`
        }
      },

      /**
       * Get the form label for custom split input
       * @param {string} paidByUserId - User ID who paid
       * @returns {string} Label for custom amount input
       */
      getCustomSplitLabel(paidByUserId) {
        if (paidByUserId === currentUserId) {
          return `${partner?.display_name || 'Partner'} owes ($)`
        } else {
          return 'You owe ($)'
        }
      }
    }
  }, [couple, currentUserId])
}
